import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Switch,
  Divider,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/store";
import {
  fetchFeatureFlags,
  createFeatureFlag,
  toggleFeatureFlag,
  deleteFeatureFlag,
  fetchFeatureFlag,
} from "@/store/featureFlagsSlice";
import { fetchProjects } from "@/store/projectsSlice";
import { fetchMatchProperties } from "@/store/matchPropertiesSlice";
import { useNotification } from "@/context/NotificationContext";
import { useApiErrorMessage } from "@/hooks/useApiErrorMessage";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PageHeader } from "@/components/PageHeader";
import { SearchField } from "@/components/SearchField";
import * as flagsApi from "@/api/featureFlags.api";
import type { FeatureFlag } from "@/types";

interface FlagForm {
  name: string;
  projectId: string;
  shortName: string;
  slug: string;
  description: string;
  payload: string;
  enabled: boolean;
}

export function Flags() {
  const { t } = useTranslation();
  const getErrorMessage = useApiErrorMessage();
  const dispatch = useAppDispatch();
  const {
    items: flags,
    current,
    loading,
  } = useAppSelector((s) => s.featureFlags);
  const { items: projects } = useAppSelector((s) => s.projects);
  const { items: matchProperties } = useAppSelector((s) => s.matchProperties);
  const { notify } = useNotification();

  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<FeatureFlag | null>(null);
  const [ruleProperty, setRuleProperty] = useState("");
  const [ruleValues, setRuleValues] = useState("");
  const [ruleRollout, setRuleRollout] = useState(100);

  const { control, handleSubmit, reset } = useForm<FlagForm>({
    defaultValues: {
      name: "",
      projectId: "",
      shortName: "",
      slug: "",
      description: "",
      payload: "",
      enabled: true,
    },
  });

  useEffect(() => {
    dispatch(fetchFeatureFlags());
    dispatch(fetchProjects());
    dispatch(fetchMatchProperties());
  }, [dispatch]);

  const filtered = flags.filter((f) =>
    f.name.toLowerCase().includes(search.toLowerCase()),
  );
  const activeCount = flags.filter((f) => f.enabled).length;

  const onCreateSubmit = async (data: FlagForm) => {
    try {
      await dispatch(
        createFeatureFlag({ ...data, projectId: data.projectId }),
      ).unwrap();
      notify(t("flags.created"), "success");
      setCreateOpen(false);
      reset();
    } catch (err) {
      notify(getErrorMessage(err), "error");
    }
  };

  const onToggle = async (flag: FeatureFlag) => {
    try {
      await dispatch(toggleFeatureFlag(flag.id)).unwrap();
      notify(t("flags.toggled"), "success");
    } catch (err) {
      notify(getErrorMessage(err), "error");
    }
  };

  const selectFlag = (flag: FeatureFlag) => {
    dispatch(fetchFeatureFlag(flag.id));
  };

  const onDelete = async () => {
    if (!deleteTarget) return;
    try {
      await dispatch(deleteFeatureFlag(deleteTarget.id)).unwrap();
      notify(t("flags.deleted"), "success");
    } catch (err) {
      notify(getErrorMessage(err), "error");
    }
    setDeleteTarget(null);
  };

  const onAddRule = async () => {
    if (!current || !ruleProperty) return;
    try {
      await flagsApi.addRule(current.id, {
        matchPropertyId: ruleProperty,
        matchPropertyValues: ruleValues
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean),
        rolloutPercentage: ruleRollout,
      });
      dispatch(fetchFeatureFlag(current.id));
      setRuleProperty("");
      setRuleValues("");
      setRuleRollout(100);
      notify(t("flags.rules.added"), "success");
    } catch (err) {
      notify(getErrorMessage(err), "error");
    }
  };

  const onDeleteRule = async (ruleId: string) => {
    if (!current) return;
    try {
      await flagsApi.deleteRule(current.id, ruleId);
      dispatch(fetchFeatureFlag(current.id));
      notify(t("flags.rules.deleted"), "success");
    } catch (err) {
      notify(getErrorMessage(err), "error");
    }
  };

  return (
    <Box>
      <PageHeader
        title={t("flags.title")}
        subtitle={t("flags.subtitle")}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              reset();
              setCreateOpen(true);
            }}
            color="secondary"
          >
            {t("flags.create")}
          </Button>
        }
      />
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 4, md: 2 }}>
          <Card>
            <CardContent sx={{ p: 2, textAlign: "center" }}>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("flags.totalFlags")}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                }}
              >
                {flags.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 4, md: 2 }}>
          <Card>
            <CardContent sx={{ p: 2, textAlign: "center" }}>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("flags.active")}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "success.main",
                }}
              >
                {activeCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 4, md: 2 }}>
          <Card>
            <CardContent sx={{ p: 2, textAlign: "center" }}>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("flags.stale")}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "error.main",
                }}
              >
                {flags.length - activeCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 5 }}>
          <SearchField
            value={search}
            onChange={setSearch}
            placeholder={t("common.search")}
            sx={{ mb: 2, maxWidth: "100%" }}
          />
          <Card>
            <CardContent sx={{ p: 0 }}>
              {loading ? (
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <Typography
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    {t("common.loading")}
                  </Typography>
                </Box>
              ) : filtered.length === 0 ? (
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <Typography
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    {t("flags.noFlags")}
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {filtered.map((flag, idx) => (
                    <ListItem
                      key={flag.id}
                      divider={idx < filtered.length - 1}
                      sx={{
                        px: 2,
                        py: 1.5,
                        cursor: "pointer",
                        bgcolor:
                          current?.id === flag.id
                            ? "action.selected"
                            : "transparent",
                      }}
                      onClick={() => selectFlag(flag)}
                    >
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: 14,
                              }}
                            >
                              {flag.name}
                            </Typography>
                            {flag.rules && flag.rules.length > 0 && (
                              <Chip
                                label={t("flags.rulesCountShort", {
                                  count: flag.rules.length,
                                })}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                            }}
                          >
                            {flag.shortName || flag.slug || ""}
                          </Typography>
                        }
                      />
                      <Switch
                        checked={flag.enabled}
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => onToggle(flag)}
                        color="primary"
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 7 }}>
          {current ? (
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 2,
                  }}
                >
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                        }}
                      >
                        {current.name}
                      </Typography>
                      <Chip
                        label={
                          current.enabled
                            ? t("flags.statusActive")
                            : t("flags.statusInactive")
                        }
                        size="small"
                        color={current.enabled ? "success" : "default"}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      {current.description}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(current.id);
                        notify(t("flags.copyIdSuccess"), "success");
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteTarget(current)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 3,
                    p: 2,
                    bgcolor: "action.hover",
                    borderRadius: 2,
                  }}
                >
                  <Switch
                    checked={current.enabled}
                    onChange={() => onToggle(current)}
                  />
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {t("flags.masterToggle")}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      {t("flags.masterToggleDesc")}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {t("flags.rules.title")}
                  </Typography>
                </Box>

                {current.rules && current.rules.length > 0 ? (
                  <List disablePadding>
                    {current.rules.map((rule) => (
                      <Card
                        key={rule.id}
                        variant="outlined"
                        sx={{ mb: 1.5, p: 2 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                              }}
                            >
                              {t("flags.rulePrefix")}{" "}
                              <Chip
                                label={
                                  rule.matchProperty?.name ||
                                  rule.matchPropertyId
                                }
                                size="small"
                                color="primary"
                                sx={{ mx: 0.5 }}
                              />{" "}
                              {t("flags.ruleInValues", {
                                values: rule.matchPropertyValues.join(", "),
                              })}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                              }}
                            >
                              {t("flags.ruleRollout", {
                                percent: rule.rolloutPercentage,
                              })}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onDeleteRule(rule.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Card>
                    ))}
                  </List>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      mb: 2,
                    }}
                  >
                    {t("flags.rules.noRules")}
                  </Typography>
                )}

                <Card variant="outlined" sx={{ p: 2, mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                    {t("flags.rules.add")}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>
                          {t("flags.rules.matchProperty")}
                        </InputLabel>
                        <Select
                          value={ruleProperty}
                          label={t("flags.rules.matchProperty")}
                          onChange={(e) => setRuleProperty(e.target.value)}
                        >
                          {matchProperties.map((mp) => (
                            <MenuItem key={mp.id} value={mp.id}>
                              {mp.name} ({mp.location})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label={t("flags.rules.values")}
                        placeholder={t("flags.rules.valuesPlaceholder")}
                        value={ruleValues}
                        onChange={(e) => setRuleValues(e.target.value)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography variant="caption">
                        {t("flags.rules.rollout")}: {ruleRollout}%
                      </Typography>
                      <Slider
                        value={ruleRollout}
                        onChange={(_, v) => setRuleRollout(v as number)}
                        min={0}
                        max={100}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={onAddRule}
                    disabled={!ruleProperty || !ruleValues}
                    sx={{ mt: 1 }}
                  >
                    {t("flags.rules.add")}
                  </Button>
                </Card>
              </CardContent>
            </Card>
          ) : (
            <Card
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 400,
              }}
            >
              <Typography
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("flags.selectFlagPrompt")}
              </Typography>
            </Card>
          )}
        </Grid>
      </Grid>
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmit(onCreateSubmit)}>
          <DialogTitle>{t("flags.create")}</DialogTitle>
          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              pt: "8px !important",
            }}
          >
            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("flags.name")}
                  required
                  fullWidth
                />
              )}
            />
            <Controller
              name="projectId"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <FormControl fullWidth required>
                  <InputLabel>{t("flags.projectId")}</InputLabel>
                  <Select {...field} label={t("flags.projectId")}>
                    {projects.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            <Controller
              name="shortName"
              control={control}
              render={({ field }) => (
                <TextField {...field} label={t("flags.shortName")} fullWidth />
              )}
            />
            <Controller
              name="slug"
              control={control}
              render={({ field }) => (
                <TextField {...field} label={t("flags.slug")} fullWidth />
              )}
            />
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("flags.description")}
                  multiline
                  rows={2}
                  fullWidth
                />
              )}
            />
            <Controller
              name="payload"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("flags.payload")}
                  multiline
                  rows={2}
                  fullWidth
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" variant="contained">
              {t("common.create")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <ConfirmDialog
        open={!!deleteTarget}
        title={t("flags.delete")}
        message={t("flags.deleteConfirm")}
        onConfirm={onDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
